import { useCallback, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import {
    SEO_META,
    HON_META,
    LEVEL_CURVE_CUMULATIVE,
    MASTERY_LEVEL,
    type SeoMeta,
    type HonMeta,
} from "@/utils/skillLabels";
import {
    pathActions, pathLevel, actionsToNextLevel, incrementPath,
    isMasteryConsumed, markMasteryConsumed,
    getFocusedPath, setFocusedPath,
} from "@/utils/trainingProgress";
import { loadV2Content, type MasteryEvent } from "@/content/combatV2Content";
import { useStoryFlash } from "@/state/storyFlash";
import { pushMasteryCutscene } from "@/state/masteryCutscene";
import { pushToast } from "@/state/toast";
import { AudioManager } from "@/engine/AudioManager";

/**
 * 훈련·연구 진행도 패널 — 서 6 + 혼 5 = 11 계열 각각의 현재 Lv 와 다음 Lv 까지 진행률.
 *
 * <p>2026-04-23 v2: WeeklyTurn · CharacterSheet · SituationDrawer 에서 공용. 런 단위로
 * 진행도가 저장된다 (localStorage — MVP). 마스터(5Lv) 달성한 계열은 burgundy 테두리로 강조.
 */
interface TrainingProgressPanelProps {
    runId: string;
    /** 간결 모드 — HUD 공간 적을 때. */
    compact?: boolean;
    /** 0-count 계열 숨기기 — 현재 훈련 중인 것만. */
    hideZero?: boolean;
}

function accentColor(token: SeoMeta["accentToken"]): string {
    switch (token) {
        case "ember": return tokens.color.ember;
        case "brass": return tokens.color.brass;
        case "steel": return tokens.color.steel;
        case "moss": return tokens.color.moss;
        case "burgundy": return tokens.color.burgundy;
        case "parchment": return tokens.color.parchment;
        default: return tokens.color.parchmentDim;
    }
}

export function TrainingProgressPanel({ runId, compact = false, hideZero = false }: TrainingProgressPanelProps) {
    // 진행도 증가 시 rerender 를 위한 바로 rebuild.
    const [bump, setBump] = useState(0);
    const pushFlash = useStoryFlash(s => s.push);
    const focusedSeo = useMemo(() => getFocusedPath(runId, "seo"), [runId, bump]);
    const focusedHon = useMemo(() => getFocusedPath(runId, "hon"), [runId, bump]);

    const toggleFocus = useCallback((pathId: string, kind: "seo" | "hon") => {
        const current = getFocusedPath(runId, kind);
        setFocusedPath(runId, kind, current === pathId ? null : pathId);
        setBump(b => b + 1);
    }, [runId]);

    const handleIncrement = useCallback(async (pathId: string, groupLabel: "서" | "혼", pathLabel: string) => {
        const { prevLevel, newLevel } = incrementPath(runId, pathId, 1);
        AudioManager.sfx(tokens.audio.sfx.buttonConfirm, 0.6);
        setBump(b => b + 1);
        if (newLevel > prevLevel) {
            // Lv up 토스트
            if (newLevel >= MASTERY_LEVEL) {
                // 마스터 달성 — 한 번만 트리거
                if (!isMasteryConsumed(runId, pathId)) {
                    markMasteryConsumed(runId, pathId);
                    const content = await loadV2Content();
                    const masteryId = `mastery.${pathId}`;
                    const event = content.masteryEvents[masteryId];
                    triggerMasteryEvent(pushFlash, pathLabel, groupLabel, event);
                }
            } else {
                pushToast("success", `${pathLabel}의 ${groupLabel} Lv ${newLevel} 해금`, { title: "훈련 결실" });
            }
        }
    }, [runId, pushFlash]);

    const seoRows = useMemo(
        () => Object.values(SEO_META).map(m => rowFor(runId, m)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [runId, bump],
    );
    const honRows = useMemo(
        () => Object.values(HON_META).map(m => rowFor(runId, m)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [runId, bump],
    );

    const visibleSeo = hideZero ? seoRows.filter(r => r.actions > 0) : seoRows;
    const visibleHon = hideZero ? honRows.filter(r => r.actions > 0) : honRows;

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: tokens.easing.decelerate }}
            // flex-shrink-0: SituationDrawer 본문이 `flex flex-col` 이라 기본 값으로는
            //   children 이 shrink 되어 overflow-hidden 과 맞물려 "타이틀만 보이고
            //   이하 내용 clip" 버그 발생. shrink-0 으로 자연 높이 유지 → drawer
            //   overflow-y-auto 가 정상 스크롤.
            className="rounded-m relative overflow-hidden shrink-0"
            style={{
                background: `linear-gradient(180deg, ${tokens.color.ink}E0, ${tokens.color.ink}B8)`,
                border: `1px solid ${tokens.color.brass}44`,
                boxShadow: `inset 0 1px 0 ${tokens.color.brass}22`,
                padding: compact ? "8px 10px" : "12px 14px",
            }}
            aria-label="훈련·연구 진행도"
        >
            <div
                aria-hidden
                className="absolute inset-y-0 left-0"
                style={{
                    width: 2,
                    background: `linear-gradient(180deg, ${tokens.color.brass}FF, ${tokens.color.brass}44)`,
                    boxShadow: `0 0 8px ${tokens.color.brass}99`,
                }}
            />
            <div
                className="font-body flex items-center gap-[6px] mb-xs pl-[6px]"
                style={{
                    color: tokens.color.brass,
                    fontSize: 15,
                    letterSpacing: "0.22em",
                    opacity: 1,
                    textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 16px`,
                }}
            >
                <span
                    aria-hidden
                    style={{
                        width: 12, height: 1,
                        background: tokens.color.brass,
                        boxShadow: `0 0 6px ${tokens.color.brass}`, opacity: 0.92,
                    }}
                />
                길의 장부 · 훈련과 연구
            </div>

            {/* 훈련 (서 6) */}
            <SectionHeader label="훈련 (서)" accent={tokens.color.ember} />
            <div className={compact ? "space-y-[4px]" : "space-y-[6px]"}>
                {visibleSeo.length === 0 ? (
                    <EmptyRow />
                ) : (
                    visibleSeo.map(row => (
                        <ProgressRow
                            key={row.id}
                            row={row}
                            compact={compact}
                            focused={focusedSeo === row.id}
                            onToggleFocus={() => toggleFocus(row.id, "seo")}
                            onIncrement={() => void handleIncrement(row.id, "서", row.label)}
                        />
                    ))
                )}
            </div>

            {/* 연구 (혼 5) */}
            <SectionHeader label="연구 (혼)" accent={tokens.color.brass} className="mt-s" />
            <div className={compact ? "space-y-[4px]" : "space-y-[6px]"}>
                {visibleHon.length === 0 ? (
                    <EmptyRow />
                ) : (
                    visibleHon.map(row => (
                        <ProgressRow
                            key={row.id}
                            row={row}
                            compact={compact}
                            focused={focusedHon === row.id}
                            onToggleFocus={() => toggleFocus(row.id, "hon")}
                            onIncrement={() => void handleIncrement(row.id, "혼", row.label)}
                        />
                    ))
                )}
            </div>
            <div
                className="mt-s pl-[6px] font-body"
                style={{
                    color: tokens.color.parchment,
                    fontSize: 14.5,
                    opacity: 1,
                    fontStyle: "italic",
                    lineHeight: 1.58,
                    textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 14px`,
                }}
            >
                {/* 2026-04-25: "각 줄의 +1 을 눌러..." 튜토리얼 프로즈 제거. 버튼 라벨 자체가 설명. */}
            </div>
        </motion.div>
    );
}

/** 마스터 달성 시 컷신 트리거 — MasteryCutsceneOverlay 로 전체 비트 재생. */
function triggerMasteryEvent(
    pushFlash: ReturnType<typeof useStoryFlash.getState>["push"],
    pathLabel: string,
    groupLabel: "서" | "혼",
    event: MasteryEvent | undefined,
) {
    // 이벤트 데이터가 있으면 풀 컷신 (NarrativeBeatPlayer). 없으면 StoryFlash 단순 토스트 폴백.
    if (event && event.beats && event.beats.length > 0) {
        pushMasteryCutscene({
            id: `mastery.${event.label}-${Date.now()}`,
            event,
            pathLabel,
            groupLabel,
        });
        return;
    }
    const title = `${pathLabel}의 ${groupLabel} · 마스터`;
    const body = "끝까지 쌓아 올린 길의 이름이 장부에 새겨진다.";
    pushFlash({
        tier: "high",
        title,
        body,
        glyph: "✦",
        accent: tokens.color.burgundy,
        durationMs: 5200,
    });
}

interface RowData {
    id: string;
    label: string;
    groupLabel: "서" | "혼";
    level: number;
    actions: number;
    accent: string;
    toNext: number;
    progressPct: number;
    isMastered: boolean;
}

function rowFor(runId: string, meta: SeoMeta | HonMeta): RowData {
    const isSeo = meta.id.startsWith("seo.");
    const level = pathLevel(runId, meta.id);
    const actions = pathActions(runId, meta.id);
    const isMastered = level >= MASTERY_LEVEL;
    const toNext = actionsToNextLevel(runId, meta.id);
    // 다음 Lv 까지 bar 진행률 — 현재 Lv 의 시작점 기준
    let progressPct = 0;
    if (isMastered) {
        progressPct = 100;
    } else if (level === 0) {
        // 아직 선택 안 함
        progressPct = 0;
    } else {
        const currStart = LEVEL_CURVE_CUMULATIVE[level - 1];
        const nextCurr = LEVEL_CURVE_CUMULATIVE[level];
        const span = Math.max(1, nextCurr - currStart);
        progressPct = Math.max(0, Math.min(100, ((actions - currStart) / span) * 100));
    }
    return {
        id: meta.id,
        label: meta.label,
        groupLabel: isSeo ? "서" : "혼",
        level,
        actions,
        accent: accentColor(meta.accentToken),
        toNext,
        progressPct,
        isMastered,
    };
}

function SectionHeader({ label, accent, className }: { label: string; accent: string; className?: string }) {
    return (
        <div
            className={`font-body pl-[6px] mb-[4px] ${className ?? ""}`}
            style={{
                color: accent,
                fontSize: 14.5,
                letterSpacing: "0.16em",
                opacity: 0.96,
                textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 14px`,
            }}
        >
            {label}
        </div>
    );
}

function EmptyRow() {
    return (
        <div
            className="font-body pl-[6px]"
            style={{
                color: tokens.color.parchment,
                fontSize: 14.5,
                opacity: 1,
                fontStyle: "italic",
                textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 14px`,
            }}
        >
            아직 시작한 길이 없다
        </div>
    );
}

function ProgressRow({
    row, compact, onIncrement, focused, onToggleFocus,
}: {
    row: RowData;
    compact: boolean;
    onIncrement?: () => void;
    focused?: boolean;
    onToggleFocus?: () => void;
}) {
    const rowHeight = compact ? 24 : 28;
    const barHeight = 3;
    return (
        <div
            className="pl-[6px] flex items-center gap-s"
            style={{ height: rowHeight }}
            title={`${row.label}의 ${row.groupLabel} · 누적 ${row.actions}회 · Lv ${row.level}${row.isMastered ? " (마스터)" : ""} · 다음까지 ${row.toNext}회${focused ? " · 지금 훈련 중" : ""}`}
        >
            {/* 포커스 토글 — ★ 눌러 이 서/혼을 "현재 훈련 중"으로 지정. WeeklyTurn TRAINING/RESEARCH 에서 자동 증가 대상. */}
            {onToggleFocus && !row.isMastered && (
                <button
                    type="button"
                    onClick={onToggleFocus}
                    style={{
                        width: 20, height: 20,
                        fontSize: 15, lineHeight: "20px",
                        background: focused ? `${tokens.color.ember}18` : `${tokens.color.parchment}10`,
                        border: focused ? `1px solid ${tokens.color.ember}55` : `1px solid ${tokens.color.parchment}33`,
                        borderRadius: 999,
                        color: focused ? tokens.color.ember : `${tokens.color.parchment}B8`,
                        cursor: "pointer",
                        flexShrink: 0,
                        padding: 0,
                        textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 12px`,
                        opacity: focused ? 1 : 0.96,
                    }}
                    title={focused ? "현재 훈련 해제" : "이 계열을 현재 훈련으로 지정"}
                >
                    {focused ? "★" : "☆"}
                </button>
            )}
            {(!onToggleFocus || row.isMastered) && (
                <div style={{ width: 20, flexShrink: 0 }} />
            )}
            {/* 이름 */}
            <div
                className="font-display"
                style={{
                    color: row.isMastered ? tokens.color.burgundy : row.level > 0 ? row.accent : tokens.color.parchment,
                    fontSize: 15,
                    letterSpacing: "0.04em",
                    width: 68,
                    flexShrink: 0,
                    textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 14px`,
                    opacity: 1,
                }}
            >
                {row.label}의 {row.groupLabel}
            </div>

            {/* Lv 배지 */}
            <div
                className="font-stat"
                style={{
                    color: row.isMastered ? tokens.color.burgundy : tokens.color.parchment,
                    fontSize: 14.5,
                    letterSpacing: "0.06em",
                    width: 56,
                    flexShrink: 0,
                    textAlign: "right",
                    textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 14px`,
                    opacity: 1,
                }}
            >
                {row.isMastered ? "✦ 마스터" : row.level > 0 ? `Lv ${row.level}` : "—"}
            </div>

            {/* 진행 bar */}
            <div
                className="flex-1 rounded-pill overflow-hidden relative"
                style={{
                    background: `${tokens.color.parchment}22`,
                    height: barHeight,
                    border: row.isMastered ? `1px solid ${tokens.color.burgundy}77` : `1px solid ${tokens.color.parchment}2A`,
                }}
            >
                <motion.div
                    className="absolute inset-y-0 left-0 rounded-pill"
                    initial={{ width: 0 }}
                    animate={{ width: `${row.progressPct}%` }}
                    transition={{ duration: 0.5, ease: tokens.easing.decelerate }}
                    style={{
                        background: row.isMastered
                            ? `linear-gradient(90deg, ${tokens.color.burgundy}, ${tokens.color.ember})`
                            : `linear-gradient(90deg, ${row.accent}DD, ${row.accent})`,
                        boxShadow: row.isMastered
                            ? `0 0 8px ${tokens.color.burgundy}`
                            : row.level > 0 ? `0 0 4px ${row.accent}AA` : "none",
                    }}
                />
            </div>

            {/* 수치 */}
            <div
                className="font-stat"
                style={{
                    color: tokens.color.parchment,
                    fontSize: 14.5,
                    letterSpacing: "0.04em",
                    width: compact ? 56 : 70,
                    flexShrink: 0,
                    textAlign: "right",
                    textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 12px`,
                }}
            >
                {row.isMastered ? `${row.actions}회` : `${row.actions}/${LEVEL_CURVE_CUMULATIVE[Math.min(row.level, MASTERY_LEVEL - 1)] ?? 24}`}
            </div>

            {/* +1 액션 버튼 — MVP. 실제 WeeklyTurn 액션 연동 전 진행도 가시화 테스트. */}
            {onIncrement && !row.isMastered && (
                <button
                    type="button"
                    onClick={onIncrement}
                    className="font-body rounded-s"
                    style={{
                        width: 38,
                        height: 24,
                        fontSize: 14,
                        letterSpacing: "0.08em",
                        color: tokens.color.parchment,
                        background: `linear-gradient(180deg, ${row.accent}20 0%, ${row.accent}12 100%)`,
                        border: `1px solid ${row.accent}55`,
                        cursor: "pointer",
                        flexShrink: 0,
                        textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 12px`,
                        boxShadow: `inset 0 1px 0 ${row.accent}22, 0 0 10px ${row.accent}12`,
                    }}
                    title={`${row.label}의 ${row.groupLabel} · 1회 훈련`}
                >
                    +1
                </button>
            )}
            {row.isMastered && (
                <div
                    style={{
                        width: 38,
                        height: 24,
                        fontSize: 14,
                        color: tokens.color.burgundy,
                        textAlign: "center",
                        lineHeight: "24px",
                        flexShrink: 0,
                        textShadow: `${tokens.color.ink} 0px 2px 8px, ${tokens.color.ink} 0px 0px 12px`,
                    }}
                    title="마스터 달성"
                >
                    ✦
                </div>
            )}
        </div>
    );
}
